// src/services/adminServices.js
import { authService } from './authService';


export const dashboardService = {
  getDashboardData: async (timeRange = '30d') => {
    console.log('[DashboardService - Fetch Request]', { timeRange });
    
    try {
      const response = await authService.authenticatedFetch(`/dashboard?timeRange=${timeRange}`);
      
      if (!response.ok) {
        const error = await response.json();
        console.error('[DashboardService - Error Response]', error);
        throw new Error(error.message || 'Failed to fetch dashboard data');
      }

      const data = await response.json();
      console.log('[DashboardService - Success Response]', { 
        timeRange, 
        dataPoints: Object.keys(data).length 
      });
      
      return data;
    } catch (error) {
      console.error('[DashboardService - Error Details]', error);
      throw error;
    }
  }
};

// System Health Service
export const systemHealthService = {
  getSystemHealth: async () => {
    console.log('[SystemHealthService - Fetch Health]');
    
    try {
      const response = await authService.authenticatedFetch('/system-health');
      
      if (!response.ok) {
        const error = await response.json();
        console.error('[SystemHealthService - Error Response]', error);
        throw new Error(error.message || 'Failed to fetch system health');
      }

      const data = await response.json();
      console.log('[SystemHealthService - Success Response]', data);
      
      return data;
    } catch (error) {
      console.error('[SystemHealthService - Error Details]', error);
      throw error;
    }
  }
};

// User Management Service
export const userService = {
  // Get users with pagination and filters
  getUsers: async ({ page = 1, limit = 20, status, searchTerm, sortBy = 'createdAt', sortOrder = -1 }) => {
    console.log('[UserService - Request Params]', { 
      page, limit, status, searchTerm, sortBy, sortOrder 
    });
    
    try {
      const queryParams = new URLSearchParams({
        page: String(page),
        limit: String(limit),
        ...(status && { status }),
        ...(searchTerm && { searchTerm }),
        sortBy,
        sortOrder: String(sortOrder)
      });

      console.log('[UserService - Request URL]', `/users?${queryParams}`);

      const response = await authService.authenticatedFetch(`/users?${queryParams}`);
      
      console.log('[UserService - Response Status]', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('[UserService - Error Response]', errorData);
        throw new Error(errorData.message || 'Failed to fetch users');
      }

      const data = await response.json();
      console.log('[UserService - Success Response]', data);
      
      return data;
    } catch (error) {
      console.error('[UserService - Error Details]', error);
      throw error;
    }
  },

  //  Get User Stats for Users Page
  getUserStats: async () => {
    console.log('[UserService - Fetch Stats]');
    
    try {
      // Match the current admin route structure
      const response = await authService.authenticatedFetch('/users-stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('[UserService - Stats Error]', {
          status: response.status,
          data: errorData
        });
        throw new Error(errorData.message || 'Failed to fetch user stats');
      }

      const data = await response.json();
      console.log('[UserService - Stats Response]', data);
      
      return data;
    } catch (error) {
      console.error('[UserService - Stats Error Details]', error);
      throw error;
    }
  },

  // Activate batch of users
  activateBatch: async (userIds) => {
    console.log('[UserService - Batch Activate]', { userIds });
    
    const response = await authService.authenticatedFetch('/users/activate-batch', {
      method: 'POST',
      body: JSON.stringify({ userIds })
    });
    
    if (!response.ok) throw new Error('Failed to activate users');
    return response.json();
  },

  // Activate single user
  activateUser: async (userId) => {
    console.log('[UserService - Activate User]', { userId });
    
    const response = await authService.authenticatedFetch('/activate-user', {
      method: 'POST',
      body: JSON.stringify({ userId })
    });
    
    if (!response.ok) throw new Error('Failed to activate user');
    return response.json();
  },

  getWaitlistCities: async () => {
    const res = await authService.authenticatedFetch('/waitlist/cities');
    if (!res.ok) throw new Error('Failed to fetch waitlist cities');
    return res.json();
  },

  getWaitlistUsersByCity: async (city) => {
    const res = await authService.authenticatedFetch(`/waitlist/users?city=${encodeURIComponent(city)}`);
    if (!res.ok) throw new Error('Failed to fetch users');
    return res.json();
  },
  
  activateCityWaitlist: async (city) => {
    const res = await authService.authenticatedFetch('/waitlist/activate-city', {
      method: 'POST',
      body: JSON.stringify({ city }),
    });
    if (!res.ok) throw new Error('Failed to activate users');
    return res.json();
  },

  /**
   * Fetches the combined list of discovered regions and their specific/effective statuses.
   * @returns {Promise<{regions: Array<object>}>}
   */
    getRegionManagementData: async () => {
      console.log('[AdminService] Fetching region management data');
      try {
        // Calls GET /admin/regions/management
        const response = await authService.authenticatedFetch('/regions/management');
        if (!response.ok) {
          const errorData = await response.json();
          console.error('[AdminService] Error fetching region data:', errorData);
          throw new Error(errorData.message || 'Failed to fetch region management data');
        }
        const data = await response.json();
        console.log(`[AdminService] Fetched ${data.regions?.length || 0} region entries.`);
        return data; // Should be { regions: [...] }
      } catch (error) {
        console.error('[AdminService] getRegionManagementData Error:', error);
        throw error;
      }
    },
  
    /**
     * Updates the specific status for a given region level.
     * @param {object} regionData - Object containing level, countryCode, stateCode?, cityName?, status
     * @param {'country'|'state'|'city'} regionData.level
     * @param {string} regionData.countryCode
     * @param {string} [regionData.stateCode] - Required for state/city level
     * @param {string} [regionData.cityName] - Required for city level
     * @param {'active'|'waitlist'|'coming_soon'|'restricted'} regionData.status
     * @returns {Promise<object>} - The API response (message, updated regionStatus)
     */
    updateRegionStatus: async (regionData) => {
      console.log('[AdminService] Updating region status:', regionData);
      const { level, countryCode, stateCode, cityName, status } = regionData;
  
      // Basic validation
      if (!level || !countryCode || !status) {
          throw new Error("Missing required fields (level, countryCode, status) for updating region status.");
      }
      // ... other validation ...
  
      // --- Build the request body conditionally ---
      const requestBody = {
          level,
          countryCode,
          status,
          // Only include stateCode/cityName if they are provided and relevant for the level
          ...( (level === 'state' || level === 'city') && stateCode && { stateCode: stateCode } ),
          ...( (level === 'city') && cityName && { cityName: cityName } )
      };
      // --- End Build request body ---
  
  
      try {
        const response = await authService.authenticatedFetch('/regions/status', {
          method: 'PUT',
          body: JSON.stringify(requestBody), // Send the conditionally built body
        });
  
        const data = await response.json();
  
        if (!response.ok) {
          console.error('[AdminService] Error updating region status - Response:', data);
          throw new Error(data.message || 'Failed to update region status');
        }
  
        console.log('[AdminService] Region status update successful:', data);
        return data;
      } catch (error) {
        console.error('[AdminService] updateRegionStatus Error:', error);
        throw error;
      }
    },
    
};




// // Reports Service
// export const reportsService = {
//   getReports: async ({ page = 1, limit = 20, status = 'pending', sortBy = 'createdAt', sortOrder = -1 }) => {
//     console.log('[ReportsService - Fetch Reports]', { page, limit, status });
    
//     const queryParams = new URLSearchParams({
//       page,
//       limit,
//       status,
//       sortBy,
//       sortOrder
//     });

//     const response = await authService.authenticatedFetch(`/reports?${queryParams}`);
//     if (!response.ok) throw new Error('Failed to fetch reports');
//     return response.json();
//   }
// };

// Analytics Service
export const analyticsService = {
  getOverview: async (dateRange = '30d') => {
    console.log('[AnalyticsService - Fetch Overview]', { dateRange });
    
    const response = await authService.authenticatedFetch(`/analytics/overview?range=${dateRange}`);
    if (!response.ok) throw new Error('Failed to fetch analytics');
    return response.json();
  }
};

// Admin Settings Service
export const adminSettingsService = {
  async createAdmin(adminData) {
    console.log('[AdminSettingsService - Create Admin]', {
      ...adminData,
      password: '***'
    });

    try {
      const response = await authService.authenticatedFetch('/admins', {
        method: 'POST',
        body: JSON.stringify(adminData)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to create admin');

      console.log('[AdminSettingsService - Admin Created]', { 
        adminId: data.admin.id 
      });

      return data;
    } catch (error) {
      console.error('[AdminSettingsService - Create Admin Error]:', error);
      throw error;
    }
  },

  async updateAdminAccount(adminId, updateData) {
    console.log('[AdminSettingsService - Update Admin]', { 
      adminId, 
      updateData 
    });

    try {
      const response = await authService.authenticatedFetch(`/admins/${adminId}`, {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to update admin account');

      console.log('[AdminSettingsService - Admin Updated]', { 
        adminId 
      });

      return data;
    } catch (error) {
      console.error('[AdminSettingsService - Update Admin Error]:', error);
      throw error;
    }
  },

  async updateOwnAccount(updateData) {
    console.log('[AdminSettingsService - Update Own Account]', { 
      updateData 
    });

    try {
      const response = await authService.authenticatedFetch('/account', {
        method: 'PUT',
        body: JSON.stringify(updateData)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to update account');

      console.log('[AdminSettingsService - Account Updated]');
      return data;
    } catch (error) {
      console.error('[AdminSettingsService - Update Account Error]:', error);
      throw error;
    }
  },

  async changePassword(passwordData) {
    console.log('[AdminSettingsService - Change Password]');

    try {
      const response = await authService.authenticatedFetch('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify(passwordData)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to change password');

      console.log('[AdminSettingsService - Password Changed]');
      return data;
    } catch (error) {
      console.error('[AdminSettingsService - Change Password Error]:', error);
      throw error;
    }
  },

  async getAdmins() {
    console.log('[AdminSettingsService - Get Admins]');
  
    try {
      const response = await authService.authenticatedFetch('/admins');
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to fetch admins');
  
      console.log('[AdminSettingsService - Admins Retrieved]', { 
        count: data.admins?.length 
      });
  
      // Sort by createdAt if not already sorted by the backend
      const sortedAdmins = data.admins.sort((a, b) => 
        new Date(b.createdAt) - new Date(a.createdAt)
      );
  
      return { ...data, admins: sortedAdmins };
    } catch (error) {
      console.error('[AdminSettingsService - Get Admins Error]:', error);
      throw error;
    }
  },

  async toggleAdminStatus(adminId, status) {
    console.log('[AdminSettingsService - Toggle Admin Status]', { 
      adminId, 
      status 
    });

    try {
      const response = await authService.authenticatedFetch(`/admins/${adminId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to update admin status');

      console.log('[AdminSettingsService - Status Updated]', { 
        adminId, 
        newStatus: status 
      });

      return data;
    } catch (error) {
      console.error('[AdminSettingsService - Toggle Status Error]:', error);
      throw error;
    }
  },

  async deleteAdmin(adminId) {
    console.log('[AdminSettingsService - Delete Admin]', { adminId });

    try {
      const response = await authService.authenticatedFetch(`/admins/${adminId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to delete admin');

      console.log('[AdminSettingsService - Admin Deleted]', { adminId });
      return data;
    } catch (error) {
      console.error('[AdminSettingsService - Delete Admin Error]:', error);
      throw error;
    }
  },

  async getSecurityLogs() {
    console.log('[AdminSettingsService - Get Security Logs]');

    try {
      const response = await authService.authenticatedFetch('/security/logs');
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to fetch security logs');

      console.log('[AdminSettingsService - Logs Retrieved]', { 
        count: data.logs?.length 
      });

      return data;
    } catch (error) {
      console.error('[AdminSettingsService - Get Logs Error]:', error);
      throw error;
    }
  },

  async updateSecuritySettings(settings) {
    console.log('[AdminSettingsService - Update Security Settings]', { 
      settings 
    });

    try {
      const response = await authService.authenticatedFetch('/security/settings', {
        method: 'PUT',
        body: JSON.stringify(settings)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to update security settings');

      console.log('[AdminSettingsService - Security Settings Updated]');
      return data;
    } catch (error) {
      console.error('[AdminSettingsService - Update Security Settings Error]:', error);
      throw error;
    }
  }
};